/* eslint-disable no-param-reassign */
import baseAxios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { getCurrentUserAuthorization } from './global';
import API_URL from '../apiconfig';

const axios: AxiosInstance = baseAxios.create({
  baseURL: API_URL,
  timeout: 1000 * 30,
  headers: {
    'Content-Type': 'application/json',
    // Skip ngrok's browser warning page for API requests
    'ngrok-skip-browser-warning': 'true',
  },
});

const onRequest = async (config: AxiosRequestConfig): Promise<any> => {
  const authorization = await getCurrentUserAuthorization();
  if (config.headers) {
    if (authorization) {
      config.headers.Authorization = authorization;
    }
    config.headers['x-porter-source'] = 'customer-web';
    // Skip ngrok's browser warning page for API requests
    config.headers['ngrok-skip-browser-warning'] = 'true';
  }
  return config;
};

axios.interceptors.request.use(onRequest);

type RetriableConfig = InternalAxiosRequestConfig & { authRetried?: boolean };

// A 401 from the auth guard usually means the cached Firebase ID token is
// stale (expired or clock skew) even though the SDK still considers it valid.
// Force a refresh and retry the request once. Access-denial 401s carry an
// application `code` and are passed through untouched.
const onResponseError = async (error: AxiosError<{ code?: string }>) => {
  const config = error.config as RetriableConfig | undefined;
  const isAuthGuard401 =
    error.response?.status === 401 && !error.response.data?.code;
  if (!config || !isAuthGuard401 || config.authRetried) {
    throw error;
  }
  const authorization = await getCurrentUserAuthorization(true);
  if (!authorization) throw error;

  config.authRetried = true;
  config.headers.Authorization = authorization;
  return axios.request(config);
};

axios.interceptors.response.use(response => response, onResponseError);

export type { AxiosResponse } from 'axios';
export { isAxiosError } from 'axios';

// Sentry fingerprint tag — axios stacks are all node_modules and group into one issue
export const getErrorStatusTag = (err: unknown) =>
  baseAxios.isAxiosError(err)
    ? String(err.response?.status ?? 'network')
    : 'unknown';

export default axios;
